import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Container, Text, Grid, Link, Box
} from 'components/ui';
import ComponentList from 'components/Content/ComponentList';
import contentConstants from 'constants/content';
import Empty from 'constants/empty';
import Breadcrumb from 'components/Content/Breadcrumb';
import Divider from 'components/Content/Divider';
import Action from 'components/Content/Action';
import { shouldRenderSmsTile } from 'utils/customerServiceSms';
import {
    fontWeights, fontSizes, lineHeights, space, mediaQueries
} from 'style/config';

const { CONTEXTS, RENDERING_TYPE, COMPONENT_TYPES } = contentConstants;
const ActionLink = Action(Link);

const CONTACT_RENDERING_TYPES = [RENDERING_TYPE.CS_CHAT_WITH_US, RENDERING_TYPE.CS_TEXT_US, RENDERING_TYPE.CS_CALL_US];

// Synthetic CMS item for the SMS "Text Us" tile injected client-side when the
// feature flag is enabled. `type` routes it through CustomRendering;
// `renderingType` selects the TextUs component.
const SMS_TILE_ITEM = {
    type: COMPONENT_TYPES.CUSTOM_RENDERING,
    renderingType: RENDERING_TYPE.CS_TEXT_US,
    sid: 'cs-text-us-preview'
};

// Places the SMS tile in the contact strip right after "Chat with Us"
// (Chat | Text | Call), falling back to the end when no chat tile is present.
const insertSmsTile = items => {
    const chatIndex = items.findIndex(item => item.renderingType === RENDERING_TYPE.CS_CHAT_WITH_US);

    if (chatIndex === -1) {
        return [...items, SMS_TILE_ITEM];
    }

    const next = [...items];
    next.splice(chatIndex + 1, 0, SMS_TILE_ITEM);

    return next;
};

// Removes any CMS-authored SMS tile and re-adds it (client-side) only when gated on,
// so the tile's visibility is driven purely by the feature flag — never by the CMS,
// and never present in the SSR/SEO output.
const resolveSourceItems = (content, showSmsTile) => {
    const hasCmsSmsTile = (content.content || Empty.Array).some(item => item.renderingType === RENDERING_TYPE.CS_TEXT_US);
    const cmsItems = hasCmsSmsTile ? content.content.filter(item => item.renderingType !== RENDERING_TYPE.CS_TEXT_US) : content.content;

    return showSmsTile ? insertSmsTile(cmsItems || Empty.Array) : cmsItems;
};

const LayoutCs = ({ content, navigation = null, breadcrumbs = null, localization }) => {
    // The SMS "Text Us" tile is governed entirely by the feature flag and rendered
    // client-side only, so it is never part of the server-rendered (SSR/SEO) HTML.
    // See utils/customerServiceSms.
    const [showSmsTile, setShowSmsTile] = useState(false);

    useEffect(() => {
        setShowSmsTile(shouldRenderSmsTile());
    }, []);

    // Visibility is driven purely by the feature flag, resolved client-side only
    // so the tile is never part of the SSR/SEO output.
    const sourceItems = resolveSourceItems(content, showSmsTile);

    const contactTileCount = (sourceItems || Empty.Array).filter(item => CONTACT_RENDERING_TYPES.includes(item.renderingType)).length;

    // Derive a new array (without mutating the content prop) so the contact strip tiles
    // render as equal-width columns: Chat | Call = 2 columns, Chat | Text | Call = 3 columns.
    const items =
        contactTileCount > 1
            ? sourceItems.map(item =>
                CONTACT_RENDERING_TYPES.includes(item.renderingType)
                    ? { ...item, gridColumn: ['span 6 !important', `span ${contactTileCount >= 3 ? 2 : 3} !important`] }
                    : item
            )
            : sourceItems;

    const isRoot = breadcrumbs ? breadcrumbs[0].action.isCurrent : true;
    const useSideNav = items && !isRoot;
    const LayoutComponent = useSideNav ? Grid : Box;

    return (
        <Container>
            {breadcrumbs && (
                <Breadcrumb
                    breadcrumbs={breadcrumbs}
                    localization={localization?.breadcrumb}
                    fontSize={fontSizes['sm-bg']}
                />
            )}
            <LayoutComponent css={useSideNav && styles.grid}>
                <Grid
                    css={styles.content}
                    gap={useSideNav && [4, 5]}
                    columnGap={!useSideNav && [4, 5]}
                >
                    {items && (
                        <ComponentList
                            enablePageRenderTracking={true}
                            trackingCount={1}
                            context={CONTEXTS.CONTAINER}
                            page='customer-service'
                            items={items}
                            removeLastItemMargin={true}
                            removeFirstItemMargin={true}
                        />
                    )}
                </Grid>
                {navigation && (
                    <>
                        {isRoot ? (
                            <>
                                <Divider />
                                <Text
                                    is='h3'
                                    css={styles.header}
                                    fontSize={[fontSizes['md-bg'], fontSizes['lg-bg']]}
                                >
                                    {localization.allTopics}
                                </Text>

                                {(navigation.items || []).map(parent => (
                                    <div
                                        key={`navParent_${parent.label}`}
                                        role='navigation'
                                        aria-label={parent.label}
                                    >
                                        <ActionLink action={parent.action}>
                                            <Text
                                                is='h4'
                                                css={styles.header}
                                                fontSize={[fontSizes['base-bg'], fontSizes['md-bg']]}
                                                marginY={space[4]}
                                                marginTop={space[6]}
                                            >
                                                {parent.label}
                                            </Text>
                                        </ActionLink>
                                        <Grid
                                            columns={[1, 4]}
                                            gap={5}
                                            rowGap={4}
                                        >
                                            {(parent.items || []).map(child => (
                                                <ActionLink
                                                    key={`navChild_${child.label}`}
                                                    action={child.action}
                                                >
                                                    <Text lineHeight={lineHeights.tight}>{child.label}</Text>
                                                </ActionLink>
                                            ))}
                                        </Grid>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div css={styles.sideNav}>
                                {useSideNav && (
                                    <Box css={styles.hideDivider}>
                                        <Divider />
                                    </Box>
                                )}
                                <Text
                                    is='h4'
                                    fontSize={[fontSizes['base-bg'], fontSizes['md-bg']]}
                                    fontWeight={fontWeights.demiBold}
                                    marginY={space[4]}
                                    marginTop={0}
                                >
                                    {navigation.label}
                                </Text>
                                <Grid
                                    columns={!items ? [1, 4] : 1}
                                    gap={5}
                                    rowGap={4}
                                    role='navigation'
                                    aria-label={navigation.label}
                                >
                                    {(navigation.items || []).map(child => (
                                        <ActionLink
                                            key={`navChild_${child.label}`}
                                            action={child.action}
                                        >
                                            <Text
                                                css={child.action.isCurrent && styles.active}
                                                lineHeight={lineHeights.tight}
                                            >
                                                {child.label}
                                            </Text>
                                        </ActionLink>
                                    ))}
                                </Grid>
                            </div>
                        )}
                    </>
                )}
            </LayoutComponent>
        </Container>
    );
};

const styles = {
    header: {
        fontWeight: fontWeights.demiBold,
        lineHeight: lineHeights.tight,
        display: 'block'
    },
    grid: {
        gridTemplateAreas: '"content" "sidebar"',
        columns: 1,
        [mediaQueries.sm]: {
            gridTemplateColumns: '208px 1fr',
            display: 'inline-grid',
            gridTemplateAreas: '"sidebar content"',
            columnGap: space[7]
        }
    },
    sideNav: {
        gridArea: 'sidebar'
    },
    content: {
        gridArea: 'content',
        gridTemplateColumns: 'repeat(6, 1fr)',
        '> div': {
            gridColumn: 'span 6'
        }
    },
    active: {
        fontWeight: fontWeights.demiBold
    },
    hideDivider: {
        [mediaQueries.sm]: {
            display: 'none'
        }
    }
};

LayoutCs.propTypes = {
    content: PropTypes.object.isRequired,
    breadcrumbs: PropTypes.array,
    navigation: PropTypes.object
};

export default wrapFunctionalComponent(LayoutCs, 'LayoutCs');
